import { useTranslation } from 'react-i18next';
import { Download, FileText, FileVideo, ImageIcon, Paperclip } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { IBugReportAttachment } from '../types';
import { formatSize } from '../utils';

function AttachmentIcon({ contentType }: { readonly contentType: string }) {
    const className = 'size-4 shrink-0 text-primary-600 dark:text-primary-300';

    if (contentType.startsWith('image/')) return <ImageIcon className={className} />;
    if (contentType.startsWith('video/')) return <FileVideo className={className} />;
    if (contentType === 'application/pdf' || contentType.startsWith('text/')) {
        return <FileText className={className} />;
    }
    return <Paperclip className={className} />;
}

/** "PNG · 145 KB" — the extension carries more than the full MIME string does. */
function describe(attachment: IBugReportAttachment): string {
    const subtype = attachment.contentType.split('/')[1] ?? '';
    const kind = subtype.split('+')[0]?.toUpperCase();
    const size = formatSize(attachment.sizeBytes);
    return kind ? `${kind} · ${size}` : size;
}

interface IMessageAttachmentListProps {
    /**
     * Optional, and defaulted, on purpose: an API older than message-scoped
     * attachments omits the field entirely, and the thread must still render.
     * The type alone does not protect this — it describes a response the
     * server may not be sending yet.
     */
    readonly attachments?: readonly IBugReportAttachment[] | undefined;
    /** Right-aligns the rows to sit under the reporter's own bubble. */
    readonly align: 'start' | 'end';
}

/**
 * Files sent with one message, rendered as named cards under its bubble.
 *
 * A card rather than a bare thumbnail: the file name and its size are what
 * someone quotes back when talking about the file, and a cropped square hides
 * both. For an attachment-only message this card IS the message, which is why
 * it carries enough weight to stand on its own.
 *
 * `downloadUrl` is a short-lived presigned URL minted per request — null when
 * the object could not be signed. The card still renders then, with the
 * download inert, rather than offering a dead link.
 */
export function MessageAttachmentList({ attachments = [], align }: IMessageAttachmentListProps) {
    const { t } = useTranslation();

    if (attachments.length === 0) return null;

    return (
        <ul
            className={cn(
                'flex max-w-[85%] flex-col gap-1',
                align === 'end' ? 'items-end' : 'items-start',
            )}
        >
            {attachments.map((attachment) => {
                const isReachable = attachment.downloadUrl !== null;

                // A thumbnail for an image, the type icon for everything else.
                // Most proof on a bug report IS a screenshot, and one glance at
                // it identifies the file far faster than reading its name.
                const isImage = attachment.contentType.startsWith('image/');

                const content = (
                    <>
                        {isImage && isReachable ? (
                            <img
                                src={attachment.downloadUrl ?? ''}
                                alt=""
                                loading="lazy"
                                className="size-8 shrink-0 rounded-md border border-border-default object-cover"
                            />
                        ) : (
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-50 dark:bg-primary-900/40">
                                <AttachmentIcon contentType={attachment.contentType} />
                            </span>
                        )}
                        <span className="flex min-w-0 flex-col text-left">
                            <span
                                className="truncate text-xs font-medium text-text-primary"
                                title={attachment.fileName}
                            >
                                {attachment.fileName}
                            </span>
                            <span className="text-2xs text-text-tertiary">
                                {describe(attachment)}
                                {isReachable
                                    ? ''
                                    : ` · ${t('bugReports.thread.attachmentUnavailable', 'unavailable')}`}
                            </span>
                        </span>
                        <Download
                            className={cn(
                                'size-3.5 shrink-0',
                                isReachable ? 'text-text-tertiary' : 'text-text-disabled',
                            )}
                            aria-hidden
                        />
                    </>
                );

                return (
                    <li key={attachment.id} className="w-full">
                        {isReachable ? (
                            <a
                                href={attachment.downloadUrl ?? undefined}
                                download={attachment.fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-surface px-2 py-1.5 transition-colors hover:bg-surface-hover"
                            >
                                {content}
                            </a>
                        ) : (
                            <span
                                className="flex items-center gap-2 rounded-lg border border-dashed border-border-default px-2 py-1.5"
                                title={t(
                                    'bugReports.thread.attachmentUnavailableHint',
                                    'This file cannot be fetched right now.',
                                )}
                            >
                                {content}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
